import { ConfirmInputModal } from "shared/components";
import { Button, Text, useToastContext } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useParams } from "react-router";
import { Flex } from "styled-system/jsx";
import {
  MembershipBySubjectAndOrganizationDocument,
  OperatorByIdDocument,
  useMembershipAdminCancelMutation,
} from "gql/generated";

type CancelMembershipButtonProps = {
  membershipLabel: string;
};

export const CancelMembershipButton = ({
  membershipLabel,
}: CancelMembershipButtonProps) => {
  const { t } = useTranslation();
  const { operatorEntityId, organizationId } = useParams();
  const { toastFactory } = useToastContext();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [cancelMembership, { loading }] = useMembershipAdminCancelMutation({
    refetchQueries: [
      OperatorByIdDocument,
      MembershipBySubjectAndOrganizationDocument,
    ],
  });

  const handleCancel = async () => {
    if (!operatorEntityId || !organizationId) return;
    try {
      await cancelMembership({
        variables: {
          operatorEntityId,
          organizationId,
        },
      });
      setIsModalOpen(false);
      toastFactory.create({
        title: t(
          "directory:OPERATOR.BACK_OFFICE.CANCEL_MEMBERSHIP.SUCCESS",
        ),
      });
    } catch {
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t(
          "directory:OPERATOR.BACK_OFFICE.CANCEL_MEMBERSHIP.ERROR",
        ),
      });
    }
  };

  return (
    <>
      <Button
        visual="destructive"
        size="sm"
        w="100%"
        onClick={() => setIsModalOpen(true)}
      >
        {t("directory:OPERATOR.BACK_OFFICE.CANCEL_MEMBERSHIP.BUTTON_TEXT", {
          membership: membershipLabel,
        })}
      </Button>
      <ConfirmInputModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={t(
          "directory:OPERATOR.BACK_OFFICE.CANCEL_MEMBERSHIP.MODAL_TITLE",
          { membership: membershipLabel },
        )}
        isMaxZIndex
        cancelButtonProps={{
          onClick: () => setIsModalOpen(false),
        }}
        saveButtonCopy={t(
          "directory:OPERATOR.BACK_OFFICE.CANCEL_MEMBERSHIP.BUTTON_TEXT",
          { membership: membershipLabel },
        )}
        saveButtonProps={{
          visual: "destructive",
          isLoading: loading,
          onClick: handleCancel,
        }}
        textToConfirm={t("core:CONFIRMATION_MODAL.I_CONFIRM")}
      >
        <Flex flexDir="column" gap="200">
          <Text color="text.mid">
            {t(
              "directory:OPERATOR.BACK_OFFICE.CANCEL_MEMBERSHIP.MODAL_DESCRIPTION",
              { membership: membershipLabel },
            )}
          </Text>
          <Flex flexDir="column" gap="50" pl="200">
            <Text visual="smallRegular" color="text.mid">
              {"• "}
              {t(
                "directory:OPERATOR.BACK_OFFICE.CANCEL_MEMBERSHIP.ACTION_REMOVE_TAGS",
                { membership: membershipLabel },
              )}
            </Text>
            <Text visual="smallRegular" color="text.mid">
              {"• "}
              {t(
                "directory:OPERATOR.BACK_OFFICE.CANCEL_MEMBERSHIP.ACTION_REMOVE_DATE",
              )}
            </Text>
            <Text visual="smallRegular" color="text.mid">
              {"• "}
              {t(
                "directory:OPERATOR.BACK_OFFICE.CANCEL_MEMBERSHIP.ACTION_CANCEL_SUBSCRIPTION",
                { membership: membershipLabel },
              )}
            </Text>
          </Flex>
        </Flex>
      </ConfirmInputModal>
    </>
  );
};
